import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Tuple } from '^/components/molecules/MyPage/Tuple';
import Text from './text';
import { useL10n } from '^/hooks';
import { GroupedSection } from '^/components/atoms/GroupedSection';
import { useParams } from 'react-router-dom';
import { useGetProjectDetail } from '^/hooks/projects/useGetProjectDetail';
import { usePopupStore } from '^/store/popup';
import * as T from '^/types';
import { useSelector } from 'react-redux';
import GCPSettings from '../GCPSettings';
import { usePatchProject } from '^/hooks/projects/usePatchProject';
import { MediaQuery } from '^/constants/styles';
import { ConfirmButton as RawConfirmButton } from '^/components/atoms/Buttons';
import { UseUploadContent, useUploadContent } from '^/hooks/useUploadContent';
import { useAttachmentsStore } from '^/store/attachmentsStore';

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingInline: '60px',
  paddingTop: '22px',
});

const SubmitButton = styled(RawConfirmButton)({
  width: 'fit-content',
  padding: '0 10px',
  height: '60px',

  [MediaQuery[T.Device.MOBILE_L]]: {
    width: '120px',
    height: '45px',
  },
});

const ButtonsWrapper = styled.div({
  marginBottom: '30px',
  textAlign: 'center',
});

const ProjectSetting = () => {
  const [l10n] = useL10n();
  const params = useParams();
  const projectId = params.id;
  const projectById = useSelector((s: T.State) => s.Projects.projects.byId);
  const uploadContent: UseUploadContent = useUploadContent();

  const project = useMemo(() => projectById[Number(projectId)], [projectId, projectById]);
  const hasProject = Boolean(project);
  const hasDroneStationIntegration: boolean = project?.integrations?.droneStation ?? false;
  const { processingAttachment } = useAttachmentsStore();

  const [projectConfig, setProjectConfig] = useState({
    withGcp: false,
    withRtk: false,
  });
  const { isLoading, refetch } = useGetProjectDetail({ projectId, hasProject });

  const { mutate: patchProject } = usePatchProject({ projectId });

  useEffect(() => {
    if (project) {
      setProjectConfig({
        withGcp: project.projectConfig?.withGcp ?? false,
        withRtk: project.projectConfig?.withRtk ?? false,
      });
    }
  }, [project]);

  const onSubmit = (data: { projectConfig: { withGcp: boolean; withRtk: boolean } }) => {
    if (processingAttachment) {
      uploadContent({
        attachmentType: T.AttachmentType.GCP,
        files: [processingAttachment],
        title: '',
      });
    }
    patchProject(
      {
        data: { projectConfig: JSON.stringify(data.projectConfig) },
      },
      {
        onSuccess: () => {
          void refetch();
        },
      }
    );
  };

  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const setMinorPopup = usePopupStore(s => s.setMinorPopup);

  const onTupleClickHandler = (type: T.ManageProjectPopupType) => {
    setPopupOptions({ manageProjectPopupType: type });
    setMinorPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
  };

  const handleSetProjectState = ({ withGcp, withRtk }: { withGcp: boolean; withRtk: boolean }) => {
    setProjectConfig({ withGcp, withRtk });
  };

  if (isLoading && !hasProject) {
    return null;
  }

  const gcpSettings: ReactNode = (
    <GCPSettings
      isEditMode={true}
      withGcp={projectConfig.withGcp}
      withRtk={projectConfig.withRtk}
      setProjectState={handleSetProjectState}
    />
  );

  return (
    <Root>
      <GroupedSection title={l10n(Text.spatialSettings)}>
        <Tuple
          data-testid="project-setting-unit-system-tuple"
          boldValue={true}
          nonEditable={false}
          value={project?.unit}
          title={l10n(Text.unitSystem)}
          onClick={() => onTupleClickHandler(T.ManageProjectPopupType.EDIT_PROJECT_UNIT_SYSTEM)}
        />
        <Tuple
          data-testid="project-setting-coordinate-system-tuple"
          boldValue={true}
          value={project?.coordinateSystem}
          title={l10n(Text.coordinateSystem)}
          onClick={() =>
            onTupleClickHandler(T.ManageProjectPopupType.EDIT_PROJECT_COORDINATE_SYSTEM)
          }
        />
      </GroupedSection>
      {hasDroneStationIntegration ? (
        <>
          <GroupedSection title={l10n(Text.moreSettings)}>
            {gcpSettings}
            <ButtonsWrapper>
              <SubmitButton
                onClick={() =>
                  onSubmit({
                    projectConfig: {
                      ...project?.projectConfig,
                      withGcp: projectConfig.withGcp,
                      withRtk: projectConfig.withRtk,
                    },
                  })
                }
              >
                {l10n(Text.applyGcpSettings)}
              </SubmitButton>
            </ButtonsWrapper>
          </GroupedSection>
        </>
      ) : null}
    </Root>
  );
};

export default ProjectSetting;
